import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { MessageCircle, Phone, Video, Users } from 'lucide-react';
import { AdvisorAvatar } from './AdvisorAvatar';

// Demo conversation data
const demoConversation = [
  {
    id: 'user-1',
    sender: 'You',
    content: "I'm considering a major career transition into tech leadership, but I'm worried about the risks. How do I know if it's the right move?",
    isUser: true
  },
  {
    id: 'beatrice-1',
    sender: 'Beatrice',
    content: "Let's analyze this systematically. What specific metrics would define success for you in this transition? Revenue impact, team size, or strategic influence?",
    isUser: false
  },
  {
    id: 'samuel-1',
    sender: 'Samuel',
    content: "I'd add that leadership isn't just about strategy—it's about your authentic self. What draws you to leadership? Understanding your 'why' will guide better decisions than any framework.",
    isUser: false
  }
];

// Typing indicator component
const TypingIndicator: React.FC<{ isVisible: boolean }> = ({ isVisible }) => {
  if (!isVisible) return null;
  
  return (
    <div className="flex items-center gap-2 px-4 py-3 rounded-2xl bg-white/10 backdrop-blur-sm border border-white/20 rounded-bl-md">
      <div className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-2 h-2 bg-white/60 rounded-full"
            animate={{
              y: [0, -5, 0],
              opacity: [0.4, 1, 0.4]
            }}
            transition={{
              duration: 1,
              repeat: Infinity,
              delay: i * 0.2
            }}
          />
        ))}
      </div>
      <span className="text-white/60 text-sm">typing...</span>
    </div>
  );
};

// Animated text component
const AnimatedText: React.FC<{ 
  text: string; 
  isVisible: boolean; 
  onComplete: () => void;
  shouldAnimate: boolean;
}> = ({ text, isVisible, onComplete, shouldAnimate }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [hasCompleted, setHasCompleted] = useState(false);
  
  useEffect(() => {
    if (!isVisible || !shouldAnimate) {
      if (!shouldAnimate) {
        setDisplayedText(text);
        if (!hasCompleted) {
          setHasCompleted(true);
          onComplete();
        }
      }
      return;
    }
    
    if (hasCompleted) return; // Don't re-animate if already completed
    
    let currentIndex = 0;
    setDisplayedText('');
    
    const timer = setInterval(() => {
      if (currentIndex <= text.length) {
        setDisplayedText(text.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(timer);
        setHasCompleted(true);
        setTimeout(onComplete, 500);
      }
    }, 30);
    
    return () => clearInterval(timer);
  }, [text, isVisible, shouldAnimate, onComplete, hasCompleted]);
  
  return <span>{displayedText}</span>;
};

export const AnimatedDemo: React.FC = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [showTyping, setShowTyping] = useState('');
  const [completedMessages, setCompletedMessages] = useState<Set<string>>(new Set());
  const [showButtons, setShowButtons] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  
  const demoRef = useRef(null);
  const isInView = useInView(demoRef, { once: true, margin: "-100px" });
  
  useEffect(() => {
    if (!isInView || hasStarted) return;
    
    // Start the animation sequence
    setHasStarted(true);
    setCurrentStep(1);
    setShowTyping('');
    setCompletedMessages(new Set());
    setShowButtons(false);
  }, [isInView, hasStarted]);
  
  const handleMessageComplete = (messageId: string) => {
    setCompletedMessages(prev => new Set([...prev, messageId]));
    setShowTyping('');
    
    // Determine next step
    if (messageId === 'user-1') {
      setTimeout(() => {
        setShowTyping('Beatrice');
        setTimeout(() => setCurrentStep(2), 1000);
      }, 500);
    } else if (messageId === 'beatrice-1') {
      setTimeout(() => {
        setShowTyping('Samuel');
        setTimeout(() => setCurrentStep(3), 1000);
      }, 500);
    } else if (messageId === 'samuel-1') {
      setTimeout(() => {
        setShowButtons(true);
      }, 1000);
    }
  };
  
  return (
    <div ref={demoRef} className="bg-gradient-to-r from-black/60 to-black/80 backdrop-blur-sm py-16 sm:py-24">
      <div className="max-w-4xl mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-orange-400 to-purple-400 bg-clip-text text-transparent">
            See It In Action
          </h2>
          <p className="text-lg sm:text-xl text-white/80 mb-8">
            Watch how multiple advisors collaborate to give you comprehensive guidance
          </p>
        </motion.div>
        
        {/* Demo Chat Interface */}
        <div className="bg-black/40 backdrop-blur-sm rounded-3xl border border-white/10 overflow-hidden">
          {/* Chat Header */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2">
                <AdvisorAvatar name="Beatrice" size="small" />
                <AdvisorAvatar name="Samuel" size="small" />
              </div>
              <div>
                <h3 className="text-white font-semibold">Beatrice, Samuel</h3>
                <p className="text-white/60 text-sm">Group Chat • 2 advisors</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-xl bg-gradient-to-r from-green-500 to-blue-500">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <div className="p-2 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500">
                <Video className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
          
          {/* Chat Messages */}
          <div className="p-4 sm:p-6 space-y-4 min-h-[400px]">
            {demoConversation.map((message, index) => {
              const shouldShow = currentStep > index;
              const isCompleted = completedMessages.has(message.id);
              const isCurrentlyTyping = currentStep === index + 1;
              
              if (!shouldShow && !isCompleted) return null;
              
              return (
                <motion.div
                  key={message.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className={`flex ${message.isUser ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`flex items-start gap-3 max-w-[85%] ${message.isUser ? 'flex-row-reverse' : ''}`}>
                    {!message.isUser && (
                      <AdvisorAvatar name={message.sender} size="small" className="flex-shrink-0" />
                    )}
                    
                    <div className={`flex flex-col ${message.isUser ? 'items-end' : 'items-start'}`}>
                      {!message.isUser && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-orange-400 font-semibold text-sm">{message.sender}</span>
                        </div>
                      )}
                      
                      <div className={`p-4 rounded-2xl ${
                        message.isUser
                          ? 'bg-gradient-to-r from-orange-500 to-purple-500 text-white'
                          : 'bg-white/10 backdrop-blur-sm text-white border border-white/20'
                      } ${message.isUser ? 'rounded-br-md' : 'rounded-bl-md'}`}>
                        <p className="leading-relaxed">
                          <AnimatedText 
                            text={message.content}
                            isVisible={shouldShow}
                            shouldAnimate={isCurrentlyTyping && !isCompleted}
                            onComplete={() => handleMessageComplete(message.id)}
                          />
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
            
            {/* Typing Indicator */}
            {showTyping && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex justify-start"
              >
                <div className="flex items-start gap-3 max-w-[85%]">
                  <AdvisorAvatar name={showTyping} size="small" className="flex-shrink-0" />
                  <div className="flex flex-col items-start">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-orange-400 font-semibold text-sm">{showTyping}</span>
                    </div>
                    <TypingIndicator isVisible={true} />
                  </div>
                </div>
              </motion.div>
            )}
          </div>
          
          {/* Action Buttons */}
          <AnimatePresence>
            {showButtons && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                className="p-4 sm:p-6 border-t border-white/10 text-center"
              >
                <p className="text-white/80 mb-4">Ready to dive deeper?</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <motion.button
                    animate={{ 
                      boxShadow: [
                        '0 0 0 rgba(34, 197, 94, 0)',
                        '0 0 20px rgba(34, 197, 94, 0.3)',
                        '0 0 0 rgba(34, 197, 94, 0)'
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-green-500 to-blue-500 text-white font-semibold hover:from-green-400 hover:to-blue-400 transition-all duration-300"
                  >
                    <Phone className="w-4 h-4" />
                    Continue in Voice
                  </motion.button>
                  
                  <motion.button
                    animate={{ 
                      boxShadow: [
                        '0 0 0 rgba(168, 85, 247, 0)',
                        '0 0 20px rgba(168, 85, 247, 0.3)',
                        '0 0 0 rgba(168, 85, 247, 0)'
                      ]
                    }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                    className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold hover:from-purple-400 hover:to-pink-400 transition-all duration-300"
                  >
                    <Video className="w-4 h-4" />
                    Continue in Video
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Demo Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={showButtons ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center"
        >
          <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
            <MessageCircle className="w-4 h-4 text-green-400" />
            <span className="text-white/80 text-sm">Real-time responses</span>
          </div>
          <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
            <Users className="w-4 h-4 text-blue-400" />
            <span className="text-white/80 text-sm">Multiple perspectives</span>
          </div>
          <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
            <Video className="w-4 h-4 text-purple-400" />
            <span className="text-white/80 text-sm">Seamless escalation</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
};